import { useEffect, useState } from 'react';
import axios from 'axios';
import { Megaphone, Twitter } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { API_BASE as API } from '@/config/api';

export default function MetroAlerts() {
  const [operatorAlerts, setOperatorAlerts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [alRes, xRes] = await Promise.allSettled([
          axios.get(`${API}/alertas/viaje`),
          axios.get(`${API}/social/efe-rancagua`, { params: { limit: 5 } }),
        ]);

        if (alRes.status === 'fulfilled' && active) {
          setOperatorAlerts(Array.isArray(alRes.value.data) ? alRes.value.data : []);
        }

        if (xRes.status === 'fulfilled' && active) {
          setPosts(Array.isArray(xRes.value.data) ? xRes.value.data : []);
        } else if (xRes.status === 'rejected' && active) {
          const err = xRes.reason;
          setError(err.response?.data?.detail || 'No se pudo cargar el feed de X.');
        }
      } catch (err) {
        if (active) setError(err.response?.data?.detail || 'No se pudieron cargar las alertas.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section className="space-y-3" data-testid="metro-alerts">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-tertiary">
          Alertas de viaje
        </h3>
        <Megaphone className="h-5 w-5 text-tertiary" aria-hidden />
      </div>

      {loading && (
        <p className="px-1 text-sm text-muted-foreground">Cargando alertas…</p>
      )}

      {!loading && operatorAlerts.length > 0 && (
        <div className="space-y-3">
          {operatorAlerts.map((a) => (
            <div
              key={a.id}
              className="block rounded-2xl border border-border/50 bg-card p-5 shadow-card"
            >
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
                  <Megaphone className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-foreground">{a.title}</p>
                  <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
                    {a.body}
                  </p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold italic text-muted-foreground">
                      Operación MuniBus
                      {(() => {
                        if (!a.createdAt) return '';
                        const d = new Date(a.createdAt);
                        if (Number.isNaN(d.getTime())) return '';
                        return ` · ${formatDistanceToNow(d, { addSuffix: true, locale: es })}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && operatorAlerts.length === 0 && (
        <p className="px-1 text-sm text-muted-foreground">
          No hay avisos del operador en este momento.
        </p>
      )}

      {!loading && posts.length > 0 && (
        <div className="space-y-2 pt-2">
          <p className="px-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
            También en X · EFE Rancagua
          </p>
          <div className="space-y-3">
            {posts.map((post) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl border border-border/50 bg-card p-5 shadow-card transition-colors hover:bg-surface-container-low/60"
              >
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tertiary-container">
                    <Twitter className="h-5 w-5 text-tertiary" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-foreground">EFE Rancagua</p>
                    <p className="mt-1 line-clamp-4 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
                      {post.text}
                    </p>
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold italic text-muted-foreground">
                        X (Twitter)
                        {(() => {
                          if (!post.createdAt) return '';
                          const d = new Date(post.createdAt);
                          if (Number.isNaN(d.getTime())) return '';
                          return ` · ${formatDistanceToNow(d, { addSuffix: true, locale: es })}`;
                        })()}
                      </span>
                      <span className="text-[10px] font-extrabold uppercase tracking-tight text-tertiary">
                        Abrir
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {!loading && operatorAlerts.length === 0 && posts.length === 0 && !error && (
        <p className="px-1 text-sm text-muted-foreground">
          No hay publicaciones recientes adicionales.
        </p>
      )}

      {error && (
        <p className="rounded-2xl bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          {error} Ver cuenta oficial en{' '}
          <a
            className="font-semibold text-primary hover:underline"
            href="https://x.com/EFE_Rancagua"
            target="_blank"
            rel="noreferrer"
          >
            @EFE_Rancagua
          </a>
          .
        </p>
      )}
    </section>
  );
}
